"use client";

import {
  onAuthStateChanged,
  signInWithPopup,
  signOut as fbSignOut,
  GoogleAuthProvider,
  type User,
} from "firebase/auth";
import { doc, getDoc, setDoc, serverTimestamp, Timestamp } from "firebase/firestore";
import { useEffect, useState } from "react";
import { getFirebase } from "./client";

export type AppRole = "instructor" | "chair" | "committee" | "external" | "admin";

export interface AppUser {
  uid: string;
  email: string;
  displayName: string;
  photoURL: string | null;
  roles: AppRole[];
  programs: string[];
  active: boolean;
  createdAt: Timestamp;
  lastLoginAt: Timestamp;
}

// Hardcoded admins for bootstrap. Replace with proper admin UI later.
const BOOTSTRAP_ADMINS: string[] = [
  // Add admin emails here. The first user matching one of these gets full roles.
];

/** React hook — returns current Firebase user + Firestore profile. */
export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<AppUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const { auth } = getFirebase();
    return onAuthStateChanged(auth, async (u) => {
      setUser(u);
      if (u) {
        const p = await ensureUserDoc(u);
        setProfile(p);
      } else {
        setProfile(null);
      }
      setLoading(false);
    });
  }, []);

  return { user, profile, loading };
}

/** Trigger Google sign-in popup. */
export async function signInWithGoogle() {
  const { auth } = getFirebase();
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: "select_account" });
  return signInWithPopup(auth, provider);
}

export async function signOut() {
  const { auth } = getFirebase();
  return fbSignOut(auth);
}

/**
 * On first login: create /users/{uid} doc with default role.
 * On subsequent logins: update lastLoginAt only.
 */
async function ensureUserDoc(user: User): Promise<AppUser> {
  const { db } = getFirebase();
  const ref = doc(db, "users", user.uid);
  const snap = await getDoc(ref);

  if (!snap.exists()) {
    const isAdmin = !!user.email && BOOTSTRAP_ADMINS.includes(user.email);
    const profile: AppUser = {
      uid: user.uid,
      email: user.email ?? "",
      displayName: user.displayName ?? user.email ?? "Unknown",
      photoURL: user.photoURL,
      // Pilot default: every new user gets ALL roles for self-testing.
      // In production: { roles: ["instructor"], programs: [] }
      roles: isAdmin
        ? ["admin", "instructor", "chair", "committee"]
        : ["instructor", "chair", "committee"],
      programs: ["demo-cs"], // Pilot: link to the demo program
      active: true,
      createdAt: serverTimestamp() as Timestamp,
      lastLoginAt: serverTimestamp() as Timestamp,
    };
    await setDoc(ref, profile);
    return profile;
  }

  await setDoc(ref, { lastLoginAt: serverTimestamp() }, { merge: true });

  // Auto-link placeholder courses to real user (by email → fallback to displayName)
  // Fire-and-forget — non-blocking
  const data = snap.data() as AppUser;
  if (data.email || data.displayName) {
    import("./bulkAssign").then(({ autoLinkPlaceholderIfMatched }) => {
      autoLinkPlaceholderIfMatched(user.uid, data.displayName ?? "", "demo-cs", data.email ?? "")
        .then(result => {
          if (result.linked) {
            console.log(`[AutoLink] ${data.displayName} → ${result.coursesUpdated} courses linked (matched by ${result.matchedBy})`);
          }
        })
        .catch(e => console.warn("AutoLink failed:", e));
    });
  }

  return data;
}

export function hasRole(profile: AppUser | null, role: AppRole): boolean {
  return !!profile?.roles?.includes(role);
}
